/**
 * SHARED SOURCE OF TRUTH for the account settings screen.
 *
 * Copied verbatim to the web client and the mobile app by
 * `yarn sync-settings` (which `yarn sync-shared` / `yarn migrate` run
 * automatically), exactly like `db/schema.ts` and `preferenceFields.ts`.
 * Do NOT edit the copies:
 *
 *   client/src/common/accountSettingsFields.ts        (generated)
 *   PoliceNarrativesMobile/common/accountSettingsFields.ts  (generated)
 *
 * Same portability constraints as preferenceFields.ts: no JSX/React, no Node
 * built-ins, no imports beyond `../db/schema`.
 *
 * SCOPE: this covers the rows that are a label + current value + an edit
 * action, which is the part web and mobile genuinely share. Two controls stay
 * platform-native because their mechanics have no counterpart on the other
 * side, and both are appended by their own platform:
 *   - Appearance (light/dark/system)  — web only; mobile has no dark mode yet
 *   - Biometric login (Face ID)       — mobile only; a device capability
 */
import {
  SubscriptionPlatformType,
  SubscriptionStatusType,
} from "../db/schema";

export type AccountSettingType =
  | "name"
  | "email"
  | "badgeId"
  | "password"
  | "department"
  | "billing"
  | "delete";

export type AccountSettingPlatform = "web" | "mobile";

export interface AccountSettingField {
  type: AccountSettingType;
  /** Row label, e.g. "Full Name" */
  label: string;
  /** Web's action button text, e.g. "Change Name" */
  buttonText: string;
  /** Sub-line under the label: the current value, or explanatory copy */
  description: string;
  /** True when this can't be edited here (agency-managed, or billed elsewhere) */
  disabled: boolean;
  /** Why it's disabled — mobile has no tooltip affordance, so it shows this */
  disabledReason?: string;
  /** Destructive styling */
  isDanger?: boolean;
  /** Mask the edit input */
  secure?: boolean;
  /**
   * Opens a modal to edit a value (name/email/badge/department/password) vs.
   * triggering navigation (billing) or a confirm-only flow (delete).
   */
  editable: boolean;
}

export interface BuildAccountSettingsOptions {
  platform: AccountSettingPlatform;
  user?: {
    name?: string | null;
    email?: string | null;
    badgeID?: string | null;
    department?: string | null;
    agency_id?: string | null;
  } | null;
  subscriptionStatus?: string | null;
  /** Web only; mobile purchases route through the Subscription screen */
  subscriptionPlatform?: string | null;
}

/**
 * Returns the account rows for one platform, already filtered and ordered.
 * Both platforms render the same rows in the same order with the same copy
 * and the same disabled rules.
 */
export const buildAccountSettings = ({
  platform,
  user,
  subscriptionStatus,
  subscriptionPlatform,
}: BuildAccountSettingsOptions): AccountSettingField[] => {
  // Officers who belong to an agency have their identity fields managed by
  // their department admin, so those rows are read-only on BOTH platforms.
  const agencyManaged = !!user?.agency_id;
  const agencyReason = "Managed by your department";

  const isTrialing =
    subscriptionStatus === SubscriptionStatusType.enum.trialing;
  const billedByApple =
    subscriptionPlatform === SubscriptionPlatformType.enum.apple;

  const value = (v?: string | null, prompt = "") => (v && v.trim()) || prompt;

  const rows: (AccountSettingField & {
    platforms: AccountSettingPlatform[];
  })[] = [
    {
      type: "name",
      label: "Full Name",
      buttonText: "Change Name",
      description: value(user?.name, "Enter your name"),
      disabled: false,
      editable: true,
      platforms: ["web", "mobile"],
    },
    {
      type: "email",
      label: "Email",
      buttonText: "Change Email",
      description: value(user?.email, "Enter your email address"),
      disabled: agencyManaged,
      disabledReason: agencyManaged ? agencyReason : undefined,
      editable: true,
      platforms: ["web", "mobile"],
    },
    {
      type: "badgeId",
      label: "Badge",
      buttonText: "Change Badge #",
      description: value(user?.badgeID, "Enter your badge ID"),
      disabled: false,
      editable: true,
      platforms: ["web", "mobile"],
    },
    {
      type: "password",
      label: "Password",
      buttonText: "Change Password",
      description: "Set a permanent password to login to your account",
      disabled: false,
      secure: true,
      editable: true,
      platforms: ["web", "mobile"],
    },
    {
      type: "department",
      label: "Department",
      buttonText: "Change Department",
      description: value(user?.department, "Enter your department"),
      disabled: agencyManaged,
      disabledReason: agencyManaged ? agencyReason : undefined,
      editable: true,
      platforms: ["web", "mobile"],
    },
    {
      type: "billing",
      label: "Billing",
      buttonText: isTrialing ? "Choose Plan" : "Manage Plan",
      description: isTrialing
        ? "Unlock full access by choosing a plan"
        : billedByApple
        ? "This subscription is billed through Apple, manage via App Store"
        : "Change or cancel your subscription",
      disabled: billedByApple || agencyManaged,
      disabledReason: billedByApple
        ? "Billed through Apple — manage it in the App Store"
        : agencyManaged
        ? agencyReason
        : undefined,
      editable: false,
      platforms: ["web", "mobile"],
    },
    {
      type: "delete",
      label: "Delete Account",
      buttonText: "Delete Account",
      description: "Permanently delete your account and all associated data",
      // Agency officers are removed by their admin, not self-serve.
      disabled: agencyManaged,
      disabledReason: agencyManaged ? agencyReason : undefined,
      isDanger: true,
      editable: false,
      // Mobile only: App Store Review Guideline 5.1.1(v) requires in-app
      // account deletion. The web app deliberately does not offer it.
      platforms: ["mobile"],
    },
  ];

  return rows
    .filter((row) => row.platforms.includes(platform))
    .map(({ platforms, ...row }) => row);
};
